import {
	AppStoreIcon,
	BadgeIcon,
	BulbIcon,
	EnergyIcon,
	LandingCopyIcon,
	MeteringIcon,
	PadLockIcon,
	PaymentIcon,
	PlayStoreIcon,
	SunIcon,
	SwitchIcon,
	ToolIcon,
	TwoWayIcon,
	UpIcon,
} from "../../assets/icons";
import { LandingPageMenu } from "./components/LandingPageMenu";
import { Navbar } from "./components/Navbar";
import styles from "./LandingPageV2.module.scss";
import Lottie from "react-lottie-player";

import CheckIcon from "../../assets/icons/landing-menu-icons/check.json";
import ButtonAnimation from "./components/buttonAnimation";
import FlipCard from "./components/flipCard";
import VerticalCarousel from "./components/VerticalCarousel";
import TrustedBy from "./components/TrustedBySection";
import { LandingPageLayout } from "./components/LandingPageLayout";
import LandingPageAccordion from "./components/LandingPageAccordion";
import Footer from "./components/LandingPageFooter";
import { ApplicationRoutes } from "../../routes";
import TextScroller from "./components/TextScroller";
import RotatingCarousel from "./components/RotatingCarousel";
import { useRef } from "react";
import { Link } from "react-router-dom";

const faqData = [
	{
		id: "1",
		question: "Is the app free?",
		answer: "Yes, Ekedp PowerApp is completely free to download and use.",
	},
	{
		id: "2",
		question: "How accurate is the real-time power usage display on the app?",
		answer: "Your smart meter sends data every 15 to 30 minutes, providing accurate updates on your energy usage!",
	},
	{
		id: "3",
		question: "Do I need to know about switching to solar to use the app?",
		answer: "No technical knowledge is needed—we’ll guide you through everything.",
	},
	{
		id: "4",
		question: "Are my payments secure?",
		answer: "Yes, we use strong security to protect all transactions.",
	},
	{
		id: "5",
		question: "Can businesses use Ekedp PowerApp?",
		answer: "Yes, Ekedp PowerApp was created for businesses of all sizes.",
	},
];

const logos = [
	{
		id: "sunpower",
		src: "../../../../images/landing-page/sun-power-logo.svg",
		alt: "Sun Power Africa",
		name: "Sun Power Africa",
	},
	{
		id: "ecovolt",
		src: "../../../../images/landing-page/eco-volt-logo.svg",
		alt: "EcoVolt - Reliable Energy Solutions",
		name: "EcoVolt",
	},
	{
		id: "brightlife",
		src: "../../../../images/landing-page/bright-life-logo.svg",
		alt: "BrightLife Solar Solutions",
		name: "BrightLife",
	},
];

const rotatingTexts = ["Energy Intelligence", "Cleaner Energy", "Reliable Energy"];

export const LandingPageV2 = () => {
	const featuresRef = useRef<HTMLDivElement>(null);
	const demoRef = useRef<HTMLDivElement>(null);
	const faqsRef = useRef<HTMLDivElement>(null);

	const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    	ref.current?.scrollIntoView({ behavior: "smooth" });
  	};

	return (
		<>
			<Navbar />
			<section className={styles.heroSection}>
				<div className={styles.innerContainer}>
					<div className={styles.heroContent}>
						<div className={styles.heroLeftSection}>
							<div className={styles.heroLeftSectionContent}>
								<LandingPageMenu scrollToSection={scrollToSection} featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} />
								<div>
									<TextScroller texts={rotatingTexts} />
									<h1 className={styles.bigHeroCaption}>All in one App</h1>
									<p className={styles.heroTagLine}>Track your energy usage, top up instantly, and switch to clean energy—all in one powerful, simple app.</p>
									<div className={styles.downloadCta}>
										<a href="/#" className={styles.downLoadLinkButton}>
											<PlayStoreIcon />
											<div>
												<p className={styles.text}>GET IT ON</p>
												<span className={styles.storeName}>Google Play</span>
											</div>
										</a>
										<a href="/#" className={styles.downLoadLinkButton}>
											<AppStoreIcon />
											<div>
												<p className={styles.text}>Download on the</p>
												<span className={styles.storeName}>App Store</span>
											</div>
										</a>
									</div>
								</div>
							</div>
						</div>
						<div className={styles.heroRightSection}>
							<div className={styles.barcodeContainer}>
								<div className={styles.barcodeBox}>
									<img src={"../../../images/landing-page/bar-code.png"} alt="bar code" />
								</div>
								<p className={styles.barcodeText}>Get the App</p>
							</div>
							<img className={styles.heroPhoneImg} src={"../../../images/landing-page/xepp-mobile-app.png"} alt="Ekedp PowerApp mobile app" />
							 <RotatingCarousel />
						</div>
					</div>
				</div>
			</section>

			{/* Features */}
			<LandingPageLayout
				ref={featuresRef}
				title="Features"
				className={`${styles.orangeBgSection} ${styles.featureSection}`}
				menuComponent={<LandingPageMenu variant="secondary" scrollToSection={scrollToSection} featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} />}
			>
				<div className={styles.inScrollContainer} ref={featuresRef}>
					<div className={styles.contentSection}>
						<EnergyIcon style={{ marginTop: 2 }} />
						<div className={styles.contentSectionBody}>
							<div>
								<h4 className={styles.contentSectionHeader}>Energy Intelligence</h4>
								<p className={styles.contentSectionText}>
									Identify peak and low usage times to manage costs effectively. Organize your payments in one place and access previous bills easily.
								</p>
							</div>
							<div className={styles.imageBox}>
								<img className={styles.image} src={"../../../images/landing-page/barchart1.png"} alt="Energy consumption chart" />
							</div>
						</div>
					</div>

					<div className={styles.contentSection}>
						<PaymentIcon style={{ marginTop: 2 }} />
						<div className={styles.contentSectionBody}>
							<div>
								<h4 className={styles.contentSectionHeader}>Payments</h4>
								<p className={styles.contentSectionText}>
									Recharge your prepaid meter using and receive your token instantly after paying. Clear your outstanding balance if you're on postpaid.
								</p>
							</div>
							<div className={styles.transactionCompleteBox}>
								<Lottie loop animationData={CheckIcon} play style={{ width: 52, height: 52 }} />
								<p className={styles.title}>Transaction Complete</p>
								<p className={styles.subTitle}>Your Payment Has Been Processed Successfully</p>
								<div className={styles.paymentTextRow}>
									<p className={styles.paymentText}>Here is your token</p>{" "}
									<div className={styles.copyBox}>
										<LandingCopyIcon /> Copy
									</div>
								</div>
								<div className={styles.tokenBox}>2124-5467-8576-7484-8474</div>
							</div>
						</div>
					</div>

					<div className={styles.contentSection}>
						<SwitchIcon style={{ marginTop: 2 }} />
						<div className={styles.contentSectionBody}>
							<div>
								<h4 className={styles.contentSectionHeader}>Switch to Clean Energy</h4>
								<p className={styles.contentSectionText}>
									Get a free load assessment to determine your power needs. Choose customized solar products and opt for flexible payment plans: pay over time
									or pay as you go for only the power you use.
								</p>
							</div>
							<div className={styles.transactionCompleteBox}>
								<div className={styles.itemSaleBox}>
									<img src={"../../../images/landing-page/solar-product-thumbnail.png"} alt="Solar product thumbnail" />
									<div className={styles.itemInfo}>
										<p className={styles.itemDescription}>Growatt SPF 3000TL HVM + 4x Amaron Quanta 200Ah Battery Solar Power Kit</p>
										<p className={styles.itemCost}>₦1,140,000.00</p>
									</div>
								</div>
								<div style={{ width: "100%", marginTop: 10 }}>
									<p className={styles.itemQuestion}>How would you like to pay?</p>
									<ButtonAnimation />
								</div>
							</div>
						</div>
					</div>

					<div className={styles.contentSection}>
						<MeteringIcon style={{ marginTop: 2 }} />
						<div className={styles.contentSectionBody}>
							<div>
								<h4 className={styles.contentSectionHeader}>Net Metering</h4>
								<p className={styles.contentSectionText}>
									Monitor your solar panel's power output, keep an eye on the excess energy returned to the grid, and calculate your profits from selling energy
									back.
								</p>
							</div>
							<div className={styles.imageBox}>
							  <img className={styles.image} src={"../../../images/landing-page/dark-bar-chart.png"} />
							</div>
						</div>
					</div>
				</div>
			</LandingPageLayout>

			{/* why People love xepp */}
			<LandingPageLayout
				title="Why People Love Ekepd PowerApp"
				subtitle="Real people. Real businesses. Real savings. The easiest way to manage your power."
				menuComponent={<LandingPageMenu variant="secondary" scrollToSection={scrollToSection} featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} />}
			>
				<div className={styles.flipCardsGrid}>
					<FlipCard
						frontImage="../../../images/landing-page/track-energy.png"
						frontTitle="Track Your Energy Usage"
						frontIcon={<TwoWayIcon height={14} />}
						frontText="Monitor your consumption patterns and spot patterns in real time, including how and when you use it."
						backText="I saved money just by seeing which appliances used the most power."
						backTextAuthor="— Sarah Adebayo, Ibadan."
						backBackground="#F2F7FF"
					/>
					<FlipCard
						frontImage="../../../images/landing-page/instant-electricity.png"
						frontTitle="Buy Electricity Instantly"
						frontIcon={<BulbIcon />}
						frontText="Top up prepaid or postpaid meters in seconds. It’s fast, secure, and stress-free."
						backText="I used to spend hours in line at the utility office just to top up the meter. Now with Ekedp PowerApp, I buy electricity in seconds, and the payment goes through immediately. We haven't had a power cut at our pharmacy due to an empty meter since."
						backTextAuthor="— Pharm Chioma A., Lagos"
						backBackground="#FFF5F2"
					/>
					<FlipCard
						frontImage="../../../images/landing-page/payment-options.png"
						frontTitle="Multiple Payment Options"
						frontIcon={<PaymentIcon />}
						frontText="Wallet, card, bank transfer, mobile money? We’ve got it all!"
						backText="I love how Ekedp PowerApp lets me pay however I want. When my card didn't work, I just switched to mobile money. So convenient!"
						backTextAuthor="— Daniel M., Accra"
						backBackground="#F2FDFF"
					/>
					<FlipCard
						frontImage="../../../images/landing-page/clean-energy-bg.png"
						frontTitle="Clean Energy made Simple"
						frontIcon={<SunIcon />}
						frontText="Tell us your power needs and get personalized solar recommendations that fit your lifestyle."
						backText="Going solar was much easier than we expected."
						backTextAuthor="— David & Amina Abd-Ahmed, Lusaka."
						backBackground="#F2F3FF"
					/>
					<FlipCard
						frontImage="../../../images/landing-page/personalized-energy-bg.png"
						frontTitle="Personalized Energy Tips"
						frontIcon={<PadLockIcon />}
						frontText="Get custom tips and usage recommendations that fit your lifestyle and budget. Small changes, big impact."
						backText="The app noticed we use most power in the evening and suggested simple timing changes that saved us money."
						backTextAuthor="— Mr and Mrs Okafor"
						backBackground="#F2FFFA"
					/>
					<FlipCard
						frontImage="../../../images/landing-page/restore-energy-bg.png"
						frontTitle="Resolve Issues Fast"
						frontIcon={<PaymentIcon />}
						frontText="Dispute bills, report faults, or request services right from the app."
						backText="Had a billing error and reported it through Xepp. Problem solved same day without a single phone call. Impressive service!"
						backTextAuthor="— Johnny O., Kumasi"
						backBackground="#F2FFFA"
					/>
				</div>
				<div className={styles.downloadCta}>
					<a href="/#" className={`${styles.downLoadLinkButton} ${styles.version2}`}>
						<PlayStoreIcon />
						<div>
							<p className={styles.text}>GET IT ON</p>
							<span className={styles.storeName}>Google Play</span>
						</div>
					</a>
					<a href="/#" className={`${styles.downLoadLinkButton} ${styles.version2}`}>
						<AppStoreIcon />
						<div>
							<p className={styles.text}>Download on the</p>
							<span className={styles.storeName}>App Store</span>
						</div>
					</a>
				</div>
			</LandingPageLayout>

			{/* market place */}
			<LandingPageLayout className={`${styles.orangeBgSection} ${styles.marketPlaceContainer}`} menuComponent={<LandingPageMenu variant="secondary" scrollToSection={scrollToSection} featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} />}>
				<div className={styles.innerContainer}>
					<div className={styles.rightSection}>
						<div>
							<div className={styles.capsule}>Marketplace</div>
							<div>
								<h2 className={styles.header}>Sunlight = Savings</h2>
								<p className={styles.subHeader}>Thinking about switching or adding solar devices? We have removed all the headaches:</p>
							</div>
						</div>
						<div className={styles.marketplaceItems}>
							<BadgeIcon />
							<div>
								<h3 className={styles.topic}>Handpicked Quality Products</h3>
								<p className={styles.subTopic}>Trusted solar equipment for your home and offices</p>
							</div>
						</div>
						<div className={styles.marketplaceItems}>
							<PaymentIcon />
							<div>
								<h3 className={styles.topic}>Flexible Payments</h3>
								<p className={styles.subTopic}>Payment plans that fit your budget, not the other way around</p>
							</div>
						</div>
						<div className={styles.marketplaceItems}>
							<ToolIcon />
							<div>
								<h3 className={styles.topic}>Professional Setup</h3>
								<p className={styles.subTopic}>Expert installation with proper warranties</p>
							</div>
						</div>
						<Link className={styles.button} to={`/${ApplicationRoutes.CUSTOMER_SIGNUP}`}>
							Shop Solar Products{" "}
						</Link>
					</div>
					<div className={styles.leftSection}>
						<img className={styles.bg1} src={"../../../images/landing-page/landing-page-3.png"} />
						<img className={styles.bg2} src={"../../../images/landing-page/landing-page-2.png"} />
					</div>
				</div>
			</LandingPageLayout>

			{/* Download The App */}
			<LandingPageLayout menuComponent={<LandingPageMenu scrollToSection={scrollToSection} variant="secondary" featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} />}>
				<div className={styles.downloadappBox}>
					<div className={styles.downloadIns}>
						<h1 className={styles.downloadHeader}>DOWNLOAD THE APP</h1>
						<p className={styles.downloadSubHeader}>join thousands of people who are taking control of their energy.</p>
						<div className={styles.downloadCta}>
							<a href="/#" className={styles.downLoadLinkButton}>
								<PlayStoreIcon />
								<div>
									<p className={styles.text}>GET IT ON</p>
									<span className={styles.storeName}>Google Play</span>
								</div>
							</a>
							<a href="/#" className={styles.downLoadLinkButton}>
								<AppStoreIcon />
								<div>
									<p className={styles.text}>Download on the</p>
									<span className={styles.storeName}>App Store</span>
								</div>
							</a>
						</div>
					</div>
					<VerticalCarousel />
				</div>
			</LandingPageLayout>

			{/* Trusted By */}

			<TrustedBy logos={logos} heading="Trusted By:" subheading="Powering Homes and Businesses across Africa" />

			{/* How it works */}
			<LandingPageLayout
				ref={demoRef}
				title="How it Works"
				subtitle="See Ekedp PowerApp in action in under 2 minutes"
				menuComponent={<LandingPageMenu scrollToSection={scrollToSection} variant="secondary" featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} />}
				className={styles.howItPlaysBg}
			>
				<img className={styles.videoPlayer} src="../../../images/landing-page/youtube-player.png" alt="youtube player" />
			</LandingPageLayout>
			{/* FAQs */}
			<LandingPageLayout ref={faqsRef} title="Got Questions?" menuComponent={<LandingPageMenu scrollToSection={scrollToSection} variant="secondary" featuresRef={featuresRef} demoRef={demoRef} faqsRef={faqsRef} /> } className={styles.greenBgSection} >
				<LandingPageAccordion items={faqData} />
				<a href="#" className={styles.accordionLink}>
					See All FAQs <UpIcon />
				</a>
			</LandingPageLayout>
			<LandingPageLayout className={styles.tagQuoteSection} containerClass={styles.containerClass}>
				<div className={styles.innerContainer}>
					<div className={styles.textContainer}>
						<h3 className={styles.title}>Join Thousands of Users</h3>
						<p className={styles.subTitle}>
							"Everyone deserves reliable, affordable power. People deserve to know where their money goes. Ekedp PowerApp makes both possible."
						</p>
						<p className={styles.authorText}>— Ekedp PowerApp Team</p>
					</div>
					<img className={styles.tagImage} src={"../../../images/landing-page/landing-page-1.png"} />
				</div>
			</LandingPageLayout>
			<Footer />
		</>
	);
};
